/**
 * API Client für JTL Descriptions Sync
 *
 * Endpoints:
 * - POST /products/{id}/descriptions/sync - Trigger sync from JTL
 * - GET /products/{id}/descriptions - Get all descriptions
 */

import apiClient from '../api-client';

const API_BASE = '/api/v1';

export interface DescriptionSyncResponse {
  success: boolean;
  synced_count: number;
  created_blocks: number;
  updated_blocks: number;
  errors?: string[];
  error?: string;
}

/**
 * Trigger sync of descriptions from JTL for a product.
 *
 * @param productId - Product ID
 * @returns Sync result with counts
 */
export async function syncDescriptionsFromJTL(
  productId: string
): Promise<DescriptionSyncResponse> {
  const response = await apiClient.post<DescriptionSyncResponse>(
    `${API_BASE}/products/${productId}/descriptions/sync`
  );
  return response.data;
}

/**
 * Single JTL description entry (per channel + language).
 */
export interface JTLDescription {
  ItemId: number;
  SalesChannelId: string;
  LanguageIso: string;
  Name: string;
  Description: string;
  ShortDescription: string;
  SeoTitleTag: string;
  SeoMetaDescription: string;
  SeoMetaKeywords: string;
  SeoPath: string;
}

export interface ProviderDescriptionsResponse {
  success: boolean;
  provider: string;
  external_id: string;
  count: number;
  data: JTLDescription[];
  channel_names: Record<string, string>;
}

/**
 * Fetch live JTL descriptions for a product (no sync).
 */
export async function getProviderDescriptions(
  productId: string
): Promise<ProviderDescriptionsResponse> {
  const response = await apiClient.get<ProviderDescriptionsResponse>(
    `${API_BASE}/products/${productId}/provider-raw-data/descriptions`
  );
  return response.data;
}

export type DescriptionBlockType =
  | 'title'
  | 'description'
  | 'short_description'
  | 'seo_meta';

export type BulletBlockType =
  | 'Bullet1'
  | 'Bullet2'
  | 'Bullet3'
  | 'Bullet4'
  | 'Bullet5';

export type KeywordsBlockType = 'SeoMetaKeywords';

export type BlockType = DescriptionBlockType | BulletBlockType | KeywordsBlockType;

export const VALID_BLOCK_TYPES: readonly BlockType[] = [
  'title',
  'description',
  'short_description',
  'seo_meta',
  'Bullet1',
  'Bullet2',
  'Bullet3',
  'Bullet4',
  'Bullet5',
  'SeoMetaKeywords',
] as const;

/**
 * Map a content-blocks `block_type` (e.g. 'seo_meta', 'bullets',
 * 'title_ger') to the JTL description block type the push endpoint
 * accepts. Only a known trailing language-code suffix is stripped
 * (language travels via `language_iso`); multi-word base types such as
 * `short_description` are preserved. Returns null for types that are NOT
 * pushable through the generic description path (e.g. 'bullets' →
 * Custom-Field slots Bullet1..5, handled separately). No silent skip —
 * callers must react.
 */
export function toDescriptionBlockType(blockType: string): DescriptionBlockType | null {
  const LANG_SUFFIXES = ['ger', 'eng', 'fre', 'spa', 'ita'] as const;
  let base = blockType;
  for (const s of LANG_SUFFIXES) {
    if (base.endsWith(`_${s}`)) {
      base = base.slice(0, -(s.length + 1));
      break;
    }
  }
  const pushable: readonly DescriptionBlockType[] = [
    'title',
    'description',
    'short_description',
    'seo_meta',
  ];
  return pushable.includes(base as DescriptionBlockType)
    ? (base as DescriptionBlockType)
    : null;
}

export interface PushDescriptionRequest {
  sales_channel_id: string;
  language_iso: string;
  block_type: BlockType;
  content: string;
}

export interface PushDescriptionResponse {
  success: boolean;
  data?: Record<string, unknown>;
  error?: string;
}

/**
 * Push a single description field to JTL and save as content block.
 */
export async function pushDescriptionToJTL(
  productId: string,
  data: PushDescriptionRequest
): Promise<PushDescriptionResponse> {
  const response = await apiClient.post<PushDescriptionResponse>(
    `${API_BASE}/products/${productId}/descriptions/push`,
    data
  );
  return response.data;
}

export interface LazySyncResponse {
  success: boolean;
  synced: boolean;
  created?: number;
  updated?: number;
  reason?: string;
}

/**
 * Trigger on-demand sync of JTL descriptions to content blocks.
 * Called automatically when product page opens.
 */
export async function lazySyncDescriptions(
  productId: string
): Promise<LazySyncResponse> {
  const response = await apiClient.post<LazySyncResponse>(
    `${API_BASE}/products/${productId}/descriptions/lazy-sync`
  );
  return response.data;
}
